import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowsClockwise, CheckCircle, Warning, CircleNotch, ShieldCheck, ShieldWarning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  match: { icon: CheckCircle, cls: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "MATCH" },
  expected_variance: { icon: ShieldCheck, cls: "text-slate-600 bg-slate-50 border-slate-200", label: "EXPECTED VARIANCE" },
  acknowledged: { icon: ShieldCheck, cls: "text-amber-700 bg-amber-50 border-amber-200", label: "ACKNOWLEDGED" },
  divergence: { icon: Warning, cls: "text-rose-700 bg-rose-50 border-rose-200", label: "DIVERGENCE" },
  not_reported: { icon: ShieldWarning, cls: "text-slate-500 bg-slate-50 border-slate-200", label: "NOT REPORTED" },
  reader_error: { icon: Warning, cls: "text-rose-700 bg-rose-50 border-rose-200", label: "READER ERROR" },
};

const PRODUCTS = [
  { id: "tavi", name: "Myval™ TAVI" },
  { id: "freedom-tkr", name: "Freedom™ TKR" },
  { id: "mericel-cart", name: "Mericel-CART" },
];

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.not_reported;
  const Icon = s.icon;
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold border ${s.cls}`}>
      <Icon size={11} />{s.label}
    </span>
  );
};

export default function ReconciliationPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [productId, setProductId] = useState("tavi");
  const [snap, setSnap] = useState(null);
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [ackFor, setAckFor] = useState(null); // {consumer_id, metric, expected, actual}
  const [ackReason, setAckReason] = useState("");
  const [ackSubmitting, setAckSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [snapRes, recRes] = await Promise.all([
        axios.get(`${API}/calc/${productId}`),
        axios.get(`${API}/calc/${productId}/reconcile`),
      ]);
      setSnap(snapRes.data);
      setRec(recRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load reconciliation");
    } finally { setLoading(false); }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const recompute = async () => {
    setRecomputing(true);
    try {
      await axios.post(`${API}/calc/${productId}/recompute`);
      toast.success("Snapshot recomputed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recompute failed");
    } finally { setRecomputing(false); }
  };

  const submitAck = async () => {
    if (!ackReason || ackReason.trim().length < 5) return toast.error("Reason must be at least 5 characters");
    setAckSubmitting(true);
    try {
      await axios.post(`${API}/calc/${productId}/reconcile/acknowledge`, {
        consumer_id: ackFor.consumer_id,
        metric: ackFor.metric,
        reason: ackReason.trim(),
      });
      toast.success("Divergence acknowledged");
      setAckFor(null); setAckReason("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Acknowledgement failed");
    } finally { setAckSubmitting(false); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

  const allowed = rec?.external_export_allowed;
  const totalConsumers = rec?.consumers?.length || 0;
  const cleanConsumers = rec?.consumers?.filter(c => c.status === "match").length || 0;

  return (
    <PageShell pageId="reconciliation" overline="Administration · Governance"
      title="Calculation Reconciliation"
      actions={(
        <div className="flex items-center gap-2">
          <select data-testid="reconciliation-product-select" value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                  className="text-xs border border-slate-300 px-3 py-2 bg-white text-slate-700">
            {PRODUCTS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {isAdmin && (
            <Button data-testid="reconciliation-recompute-btn" onClick={recompute} disabled={recomputing || loading}
                    className="text-xs bg-slate-900 hover:bg-slate-800 rounded-none">
              {recomputing ? <CircleNotch size={13} className="animate-spin" /> : <ArrowsClockwise size={13} />}
              <span className="ml-1.5">Recompute</span>
            </Button>
          )}
        </div>
      )}>

      {loading && !rec && (
        <div className="py-16 text-center text-slate-400 text-sm" data-testid="reconciliation-loading">
          Loading reconciliation…
        </div>
      )}

      {rec && (
        <>
          {/* Headline strip */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
            <div className="border border-slate-200 bg-white px-4 py-3" data-testid="reconciliation-summary-export-status">
              <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">External Export</div>
              <div className={`mt-1 flex items-center gap-1.5 text-sm font-bold ${allowed ? "text-emerald-700" : "text-rose-700"}`}>
                {allowed ? <><ShieldCheck size={16} /> Allowed</> : <><Warning size={16} /> Blocked</>}
              </div>
            </div>
            <div className="border border-slate-200 bg-white px-4 py-3">
              <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Snapshot Version</div>
              <div className="mt-1 text-sm font-bold text-slate-800">v{snap?.version} · fingerprint {snap?.fingerprint}</div>
            </div>
            <div className="border border-slate-200 bg-white px-4 py-3">
              <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Data Cutoff</div>
              <div className="mt-1 text-sm font-bold text-slate-800">{fmtDate(snap?.data_cutoff)}</div>
            </div>
            <div className="border border-slate-200 bg-white px-4 py-3">
              <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Last Calculated</div>
              <div className="mt-1 text-sm font-bold text-slate-800">{fmtDate(snap?.last_calculated_at)}</div>
            </div>
          </div>

          {!allowed && (
            <div data-testid="reconciliation-blocked-banner" className="border-l-4 border-rose-500 bg-rose-50 px-4 py-3 mb-6">
              <div className="text-sm font-bold text-rose-800 flex items-center gap-2">
                <Warning size={16} /> External report export is currently BLOCKED
              </div>
              <p className="text-xs text-rose-700 mt-1">
                {rec.unblocked_divergences} unresolved divergence(s) between the authoritative calculation service and downstream consumers.
                Reconcile below (or acknowledge with a documented reason) before exporting a submission-grade PDF, DOCX or Executive Brief.
                One-pagers remain available for internal analyst review.
              </p>
            </div>
          )}

          {/* Authoritative snapshot card */}
          <div className="border border-slate-200 bg-white mb-6" data-testid="reconciliation-snapshot-card">
            <div className="border-b border-slate-200 px-4 py-2.5 bg-slate-50">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wide">Authoritative Snapshot</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 divide-x divide-slate-200">
              <div className="px-4 py-3">
                <div className="text-[10px] uppercase text-slate-500">GMARI™</div>
                <div className="text-lg font-bold text-emerald-700">{snap?.gmari?.score}<span className="text-xs text-slate-500">/100</span></div>
                <div className="text-[10px] text-slate-500">{snap?.gmari?.band}</div>
              </div>
              <div className="px-4 py-3">
                <div className="text-[10px] uppercase text-slate-500">Target Markets</div>
                <div className="text-lg font-bold text-slate-800">{snap?.market_counts?.total_target}</div>
              </div>
              <div className="px-4 py-3">
                <div className="text-[10px] uppercase text-slate-500">Wave 1</div>
                <div className="text-lg font-bold text-slate-800">{snap?.market_counts?.wave_1}</div>
              </div>
              <div className="px-4 py-3">
                <div className="text-[10px] uppercase text-slate-500">Wave 2</div>
                <div className="text-lg font-bold text-slate-800">{snap?.market_counts?.wave_2}</div>
              </div>
              <div className="px-4 py-3">
                <div className="text-[10px] uppercase text-slate-500">Wave 3</div>
                <div className="text-lg font-bold text-slate-800">{snap?.market_counts?.wave_3}</div>
              </div>
              <div className="px-4 py-3">
                <div className="text-[10px] uppercase text-slate-500">Evidence Gaps</div>
                <div className="text-lg font-bold text-slate-800">{snap?.evidence_gaps?.total}</div>
              </div>
            </div>
          </div>

          {/* Consumer × Metric table */}
          <div className="border border-slate-200 bg-white" data-testid="reconciliation-consumer-table">
            <div className="border-b border-slate-200 px-4 py-2.5 bg-slate-50 flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                Consumer Reconciliation — {cleanConsumers} of {totalConsumers} clean
              </h3>
              <span className="text-[10px] text-slate-500">One row per consumer × metric</span>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-100">
                <tr className="text-left text-slate-600 uppercase text-[10px]">
                  <th className="px-3 py-2 font-semibold">Consumer</th>
                  <th className="px-3 py-2 font-semibold">Metric</th>
                  <th className="px-3 py-2 font-semibold text-right">Authoritative</th>
                  <th className="px-3 py-2 font-semibold text-right">Consumer</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Notes / Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rec.consumers.map((c) => (
                  c.metrics && c.metrics.length ? (
                    c.metrics.map((m, i) => (
                      <tr key={`${c.consumer_id}-${m.metric}`}
                          data-testid={`reconciliation-row-${c.consumer_id}-${m.metric}`}
                          className={m.status === "divergence" ? "bg-rose-50/30" : ""}>
                        {i === 0 && (
                          <td rowSpan={c.metrics.length} className="px-3 py-2 border-r border-slate-100 align-top">
                            <div className="font-bold text-slate-800">{c.consumer_label}</div>
                            <div className="text-[10px] text-slate-500 font-mono">{c.consumer_id}</div>
                          </td>
                        )}
                        <td className="px-3 py-2 font-mono text-slate-700">{m.metric}</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-800">{String(m.expected ?? "—")}</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-800">{String(m.actual ?? "—")}</td>
                        <td className="px-3 py-2"><StatusBadge status={m.status} /></td>
                        <td className="px-3 py-2 text-[11px] text-slate-600">
                          {m.status === "acknowledged" && (
                            <div>
                              <div className="text-slate-500">By {m.acknowledged_by || "—"}</div>
                              <div className="italic">"{m.reason}"</div>
                            </div>
                          )}
                          {m.status === "expected_variance" && <span>{m.reason}</span>}
                          {m.status === "divergence" && isAdmin && (
                            <Button data-testid={`reconciliation-ack-${c.consumer_id}-${m.metric}-btn`}
                                    onClick={() => { setAckFor({ ...m, consumer_id: c.consumer_id, consumer_label: c.consumer_label }); setAckReason(""); }}
                                    className="text-[10px] bg-amber-600 hover:bg-amber-700 px-2 py-1 h-auto rounded-none">
                              Acknowledge
                            </Button>
                          )}
                          {m.status === "divergence" && !isAdmin && <span className="text-slate-400">Admin only</span>}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr key={c.consumer_id} data-testid={`reconciliation-row-${c.consumer_id}-meta`}>
                      <td className="px-3 py-2 align-top">
                        <div className="font-bold text-slate-800">{c.consumer_label}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{c.consumer_id}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-400 italic" colSpan={4}>
                        {c.status === "reader_error" ? `Reader error: ${c.error}` : "Meta-level consumer (no numeric metrics compared)"}
                      </td>
                      <td className="px-3 py-2"><StatusBadge status={c.status} /></td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Acknowledgement dialog */}
      {ackFor && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center px-4" data-testid="reconciliation-ack-dialog"
             onClick={() => setAckFor(null)}>
          <div className="bg-white border border-slate-300 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-4 py-3 bg-slate-50">
              <h4 className="text-sm font-bold text-slate-800">Acknowledge Divergence</h4>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {ackFor.consumer_label} · <span className="font-mono">{ackFor.metric}</span> · authoritative={String(ackFor.expected)} · consumer={String(ackFor.actual)}
              </p>
            </div>
            <div className="px-4 py-4 space-y-3">
              <label className="block text-[11px] font-semibold text-slate-700">
                Reason (minimum 5 characters — will be visible in every future reconciliation view)
              </label>
              <Textarea data-testid="reconciliation-ack-reason-input"
                        value={ackReason} onChange={(e) => setAckReason(e.target.value)}
                        placeholder="e.g. Executive Insights module retains a legacy figure during a transition — will be republished after the next monitor cycle."
                        rows={4} className="text-xs" />
              <p className="text-[10px] text-slate-500">
                Acknowledgement is auto-invalidated when the snapshot version bumps (i.e. when the underlying data changes).
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button data-testid="reconciliation-ack-cancel-btn" onClick={() => setAckFor(null)} variant="ghost"
                        className="text-xs rounded-none">Cancel</Button>
                <Button data-testid="reconciliation-ack-submit-btn" onClick={submitAck} disabled={ackSubmitting || ackReason.trim().length < 5}
                        className="text-xs bg-amber-600 hover:bg-amber-700 rounded-none">
                  {ackSubmitting ? <CircleNotch size={13} className="animate-spin" /> : "Acknowledge & unblock"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
